// Comparison-period presets shared by My Customers (Welcome) and Insights.
//
// Two flavors of preset:
//   - MONTH-BASED ("last full month", "last 3 full months", "last quarter",
//     "YTD", etc.) — the current window NEVER includes the in-progress
//     calendar month. If today is May 6, "last 3 full months" means
//     Feb 1 – Apr 30, NOT Feb 6 – May 5.
//   - DAY-COUNTED ("last 90 days") — the current window ends YESTERDAY
//     (so partial-day data isn't comparing 23 hours against a full day).
//
// All dates returned in YYYY-MM-DD format with INCLUSIVE end dates, the way
// the cache APIs and customer-metrics endpoint treat them.

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth   = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addDays      = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

// Last N full calendar months ending with the month BEFORE today's month.
// today=May 6, n=3 →  current Feb 1–Apr 30, previous Nov 1–Jan 31.
function lastNFullMonths(today, n) {
  const firstOfThisMonth = startOfMonth(today);
  const currEnd   = new Date(firstOfThisMonth);
  currEnd.setDate(0); // last day of previous month
  const currStart = new Date(currEnd.getFullYear(), currEnd.getMonth() - (n - 1), 1);
  const prevEnd   = new Date(currStart);
  prevEnd.setDate(0);
  const prevStart = new Date(prevEnd.getFullYear(), prevEnd.getMonth() - (n - 1), 1);
  return { currStart, currEnd, prevStart, prevEnd };
}

// Last full calendar quarter ending before the current (in-progress)
// quarter. today=May 6 → current Q1 (Jan 1 – Mar 31), previous Q4 of last
// year (Oct 1 – Dec 31).
function lastFullQuarter(today) {
  const m = today.getMonth();
  const currQ = Math.floor(m / 3); // 0..3 — the in-progress quarter
  // currQ * 3 is the first month of the in-progress quarter; its day-0 is
  // the last day of the previous quarter.
  const prevQEnd   = new Date(today.getFullYear(), currQ * 3, 0);
  const prevQStart = new Date(prevQEnd.getFullYear(), prevQEnd.getMonth() - 2, 1);
  const prev2End   = new Date(prevQStart);
  prev2End.setDate(0);
  const prev2Start = new Date(prev2End.getFullYear(), prev2End.getMonth() - 2, 1);
  return { currStart: prevQStart, currEnd: prevQEnd, prevStart: prev2Start, prevEnd: prev2End };
}

// YTD this year vs same months last year. today=May 6 → Jan 1–Apr 30 2026
// vs Jan 1–Apr 30 2025. The two windows are NON-ADJACENT (gap is the
// rest of last year), so the metrics service must handle that case.
function ytdFullMonths(today) {
  const firstOfThisMonth = startOfMonth(today);
  const currEnd   = new Date(firstOfThisMonth);
  currEnd.setDate(0);
  // Edge case: it's January, so there's no completed month yet this year.
  // Fall back to last full month vs same month prior year.
  if (currEnd.getFullYear() !== today.getFullYear()) {
    const prevEnd   = new Date(currEnd.getFullYear() - 1, currEnd.getMonth() + 1, 0);
    const prevStart = new Date(currEnd.getFullYear() - 1, currEnd.getMonth(), 1);
    const currStart = new Date(currEnd.getFullYear(), currEnd.getMonth(), 1);
    return { currStart, currEnd, prevStart, prevEnd };
  }
  const currStart = new Date(today.getFullYear(), 0, 1);
  const prevEnd   = new Date(today.getFullYear() - 1, currEnd.getMonth() + 1, 0);
  const prevStart = new Date(today.getFullYear() - 1, 0, 1);
  return { currStart, currEnd, prevStart, prevEnd };
}

// Last N days ending YESTERDAY (so partial-day data doesn't skew the
// current side). today=May 6, n=90 → current Feb 6–May 5, prev Nov 8–Feb 5.
function lastNDays(today, n) {
  const yesterday = addDays(today, -1);
  const currEnd   = yesterday;
  const currStart = addDays(yesterday, -(n - 1));
  const prevEnd   = addDays(currStart, -1);
  const prevStart = addDays(prevEnd, -(n - 1));
  return { currStart, currEnd, prevStart, prevEnd };
}

export const COMPARISON_PRESETS = [
  {
    id:    'last_full_month',
    label: 'Last full month vs prior full month',
    short: 'Last full month',
    compute: (t) => lastNFullMonths(t, 1),
  },
  {
    id:    'last_3_full_months',
    label: 'Last 3 full months vs prior 3 full months',
    short: 'Last 3 full months',
    compute: (t) => lastNFullMonths(t, 3),
    isDefault: true,
  },
  {
    id:    'last_full_quarter',
    label: 'Last full quarter vs prior full quarter',
    short: 'Last full quarter',
    compute: lastFullQuarter,
  },
  {
    id:    'ytd_full_months',
    label: 'This year (full months) vs same period last year',
    short: 'This year (full months)',
    compute: ytdFullMonths,
  },
  {
    id:    'last_6_full_months',
    label: 'Last 6 full months vs prior 6 full months',
    short: 'Last 6 full months',
    compute: (t) => lastNFullMonths(t, 6),
  },
  {
    id:    'last_90_days',
    label: 'Last 90 days vs prior 90 days',
    short: 'Last 90 days',
    compute: (t) => lastNDays(t, 90),
  },
];

export const DEFAULT_COMPARISON_PRESET =
  COMPARISON_PRESETS.find(p => p.isDefault)?.id || 'last_3_full_months';

/**
 * Compute concrete date ranges for a preset.
 * Returns YYYY-MM-DD strings (inclusive end) plus human-readable labels.
 *
 *   const r = computeComparisonRange('last_3_full_months');
 *   // {
 *   //   presetId, label, short,
 *   //   currentStart:  '2026-02-01',  currentEnd:  '2026-04-30',
 *   //   previousStart: '2025-11-01',  previousEnd: '2026-01-31',
 *   //   currentLabel:  'Feb 1 – Apr 30, 2026',
 *   //   previousLabel: 'Nov 1, 2025 – Jan 31, 2026',
 *   // }
 */
export function computeComparisonRange(presetId, today = new Date()) {
  const preset =
    COMPARISON_PRESETS.find(p => p.id === presetId) ||
    COMPARISON_PRESETS.find(p => p.isDefault);
  const r = preset.compute(today);
  return {
    presetId: preset.id,
    label:    preset.label,
    short:    preset.short,
    currentStart:  ymd(r.currStart),
    currentEnd:    ymd(r.currEnd),
    previousStart: ymd(r.prevStart),
    previousEnd:   ymd(r.prevEnd),
    currentLabel:  humanizeRange(r.currStart, r.currEnd),
    previousLabel: humanizeRange(r.prevStart, r.prevEnd),
  };
}

/** "Feb 1 – Apr 30, 2026" or "Nov 1, 2025 – Jan 31, 2026" if it crosses a year. */
export function humanizeRange(start, end) {
  const sameYear = start.getFullYear() === end.getFullYear();
  const fmtFull  = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const fmtNoYr  = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return sameYear
    ? `${fmtNoYr(start)} – ${fmtFull(end)}`
    : `${fmtFull(start)} – ${fmtFull(end)}`;
}

const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const isWeekend = (d) => {
  const day = d.getDay();
  return day === 0 || day === 6;
};

/** Monday of the calendar week containing `d`. */
function startOfBusinessWeek(d) {
  const x = stripTime(d);
  const dow = x.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  x.setDate(x.getDate() + diff);
  return x;
}

/**
 * Latest day with complete cached data. VitalEngine refresh runs at 3 AM,
 * so today's partial day is excluded. Weekends are skipped when walking back
 * from yesterday — Sat/Sun carry little activity.
 */
function latestDataDay(today = new Date()) {
  let d = stripTime(addDays(today, -1));
  while (isWeekend(d)) d = addDays(d, -1);
  return d;
}

function businessDaysBetween(start, end) {
  const days = [];
  for (let d = stripTime(start); d <= stripTime(end); d = addDays(d, 1)) {
    if (!isWeekend(d)) days.push(stripTime(d));
  }
  return days;
}

/** "Monday" or "Mon–Wed, Jun 9" for a list of business days. */
function labelBusinessDays(days) {
  if (!days.length) return '—';
  if (days.length === 1) {
    return days[0].toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  }
  const wd = (d) => d.toLocaleDateString('en-US', { weekday: 'short' });
  const endPart = days[days.length - 1].toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  return `${wd(days[0])}–${wd(days[days.length - 1])}, ${endPart}`;
}

/**
 * Business days elapsed this week (Mon → latest complete day) vs the same
 * weekdays last week. Weekends are ignored; today is never included.
 *
 * Example (today = Tue): compare Monday vs prior Monday (1 business day).
 * Example (today = Thu): compare Mon–Wed vs prior Mon–Wed.
 */
export function computeWeekComparisonRange(today = new Date()) {
  const t = stripTime(today);
  const dataThrough = latestDataDay(t);
  const thisWeekMonday = startOfBusinessWeek(t);

  const currentDays = dataThrough >= thisWeekMonday
    ? businessDaysBetween(thisWeekMonday, dataThrough)
    : [];
  const previousDays = currentDays.map((d) => addDays(d, -7));

  const currStart = currentDays[0] || thisWeekMonday;
  const currEnd = currentDays[currentDays.length - 1] || dataThrough;
  const prevStart = previousDays[0] || addDays(thisWeekMonday, -7);
  const prevEnd = previousDays[previousDays.length - 1] || addDays(dataThrough, -7);

  const dataThroughLabel = dataThrough.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  let periodNote = null;
  if (currentDays.length === 0) {
    periodNote = `Data refreshes at 3 AM — nothing for this week yet. Latest complete day is ${dataThroughLabel}.`;
  } else if (currentDays.length === 1) {
    periodNote = `Comparing ${currentDays[0].toLocaleDateString('en-US', { weekday: 'long' })} to the same day last week (data through ${dataThroughLabel}).`;
  } else {
    periodNote = `Comparing ${currentDays.length} business days to the same weekdays last week (data through ${dataThroughLabel}).`;
  }

  return {
    presetId: 'this_week_business_days',
    label: 'Business days this week vs same weekdays last week',
    short: 'This week',
    currentStart: ymd(currStart),
    currentEnd: ymd(currEnd),
    previousStart: ymd(prevStart),
    previousEnd: ymd(prevEnd),
    currentLabel: labelBusinessDays(currentDays),
    previousLabel: labelBusinessDays(previousDays),
    businessDayCount: currentDays.length,
    dataThrough: ymd(dataThrough),
    periodNote,
    hasBusinessDays: currentDays.length > 0,
  };
}

/** Round-trip helper for tests / debugging. */
export { startOfMonth, endOfMonth, lastNFullMonths, lastFullQuarter, ytdFullMonths, lastNDays };
